import { AlertCircle, Mail, RefreshCw } from 'lucide-react'

import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/app/components/ui/carousel'
import { Input } from '@/app/components/ui/input'
import type { ReportMailSenderSetting } from '@/app/services/settings.service'

export interface ReportMailSenderFieldErrors {
  address?: string
  name?: string
  username?: string
  password?: string
}

interface ReportMailSenderSettingsCardProps {
  senders: ReportMailSenderSetting[]
  errors: Record<number, ReportMailSenderFieldErrors>
  isLoading: boolean
  isError: boolean
  isDisabled: boolean
  onChange: (
    sucursalId: number,
    field:
      | 'report_mail_from_address'
      | 'report_mail_from_name'
      | 'report_mail_username'
      | 'report_mail_password',
    value: string,
  ) => void
  onRetry: () => void
}

export function ReportMailSenderSettingsCard({
  senders,
  errors,
  isLoading,
  isError,
  isDisabled,
  onChange,
  onRetry,
}: ReportMailSenderSettingsCardProps) {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-5 w-5 text-primary" />
          Correo de reportes
        </CardTitle>
        <CardDescription>
          Configura las credenciales y el remitente utilizado para enviar archivos XLSX desde cada sucursal. El servidor SMTP es global.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" aria-label="Cargando remitentes">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-56 animate-pulse rounded-lg border border-card-border bg-muted/40" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
            <AlertCircle className="h-7 w-7 text-destructive" />
            <p className="text-sm text-muted-foreground">No fue posible cargar los remitentes de las sucursales.</p>
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="h-4 w-4" />
              Reintentar
            </Button>
          </div>
        ) : senders.length === 0 ? (
          <div className="rounded-lg border border-dashed border-card-border p-8 text-center text-sm text-muted-foreground">
            No hay sucursales disponibles para configurar.
          </div>
        ) : (
          <div className="relative">
            <Carousel opts={{ align: 'start', loop: false }} className="w-full">
              <CarouselContent className="-ml-2 md:-ml-4">
                {senders.map((sender) => {
                  const fieldErrors = errors[sender.id]
                  const passwordAvailable = Boolean(
                    sender.report_mail_password?.trim() || sender.report_mail_password_configured,
                  )
                  const configured = Boolean(
                    sender.report_mail_username?.trim()
                    && sender.report_mail_from_address?.trim()
                    && sender.report_mail_from_name?.trim()
                    && passwordAvailable,
                  )

                  return (
                    <CarouselItem
                      key={sender.id}
                      className="pl-2 md:basis-1/2 md:pl-4 lg:basis-1/3"
                    >
                      <div className="h-full space-y-4 rounded-lg border border-card-border bg-card p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-card-foreground">{sender.name}</p>
                            <p className="text-xs text-muted-foreground">Código: {sender.code}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant={sender.is_active ? 'default' : 'secondary'}>
                              {sender.is_active ? 'Activa' : 'Inactiva'}
                            </Badge>
                            <Badge variant={configured ? 'default' : 'outline'}>
                              {configured ? 'Correo configurado' : 'Correo incompleto'}
                            </Badge>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor={`report-mail-username-${sender.id}`} className="text-sm font-medium text-card-foreground">
                            Usuario SMTP
                          </label>
                          <Input
                            id={`report-mail-username-${sender.id}`}
                            value={sender.report_mail_username ?? ''}
                            placeholder="usuario@sucursal.com"
                            disabled={isDisabled}
                            aria-invalid={Boolean(fieldErrors?.username)}
                            onChange={(event) => onChange(sender.id, 'report_mail_username', event.target.value)}
                          />
                          {fieldErrors?.username && <p className="text-xs text-destructive">{fieldErrors.username}</p>}
                        </div>

                        <div className="space-y-2">
                          <label htmlFor={`report-mail-password-${sender.id}`} className="text-sm font-medium text-card-foreground">
                            Contraseña SMTP
                          </label>
                          <Input
                            id={`report-mail-password-${sender.id}`}
                            type="password"
                            autoComplete="new-password"
                            value={sender.report_mail_password ?? ''}
                            placeholder="****"
                            disabled={isDisabled}
                            aria-invalid={Boolean(fieldErrors?.password)}
                            onChange={(event) => onChange(sender.id, 'report_mail_password', event.target.value)}
                          />
                          {fieldErrors?.password && (
                            <p className="text-xs text-destructive">{fieldErrors.password}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label htmlFor={`report-mail-address-${sender.id}`} className="text-sm font-medium text-card-foreground">
                            Correo remitente
                          </label>
                          <Input
                            id={`report-mail-address-${sender.id}`}
                            type="email"
                            value={sender.report_mail_from_address ?? ''}
                            placeholder="reportes@sucursal.com"
                            disabled={isDisabled}
                            aria-invalid={Boolean(fieldErrors?.address)}
                            onChange={(event) => onChange(sender.id, 'report_mail_from_address', event.target.value)}
                          />
                          {fieldErrors?.address && <p className="text-xs text-destructive">{fieldErrors.address}</p>}
                        </div>

                        <div className="space-y-2">
                          <label htmlFor={`report-mail-name-${sender.id}`} className="text-sm font-medium text-card-foreground">
                            Nombre remitente
                          </label>
                          <Input
                            id={`report-mail-name-${sender.id}`}
                            value={sender.report_mail_from_name ?? ''}
                            placeholder={sender.name}
                            disabled={isDisabled}
                            aria-invalid={Boolean(fieldErrors?.name)}
                            onChange={(event) => onChange(sender.id, 'report_mail_from_name', event.target.value)}
                          />
                          {fieldErrors?.name && <p className="text-xs text-destructive">{fieldErrors.name}</p>}
                        </div>
                      </div>
                    </CarouselItem>
                  )
                })}
              </CarouselContent>
              <div className="hidden md:block">
                <CarouselPrevious type="button" className="-left-4" />
                <CarouselNext type="button" className="-right-4" />
              </div>
            </Carousel>
            <p className="mt-4 text-center text-xs text-muted-foreground md:hidden">
              Desliza para ver más sucursales
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
